/**
 * Constants for Power Reporting components
 * Centralized configuration values to avoid magic numbers/strings
 */

// Auto-refresh interval in milliseconds (30 seconds)
export const AUTO_REFRESH_INTERVAL_MS = 30000;

// Permission type ID for power reporting access
export const POWER_REPORT_PERMISSION_TYPE_ID = 11;

// Power status codes
export const POWER_STATUS = {
  UNEXECUTED: 'U',
  UNEXECUTED_TRANSFER: 'UT',
  VOID: 'V',
  EXECUTED: 'E',
  TRANSFER: 'T',
  FORFEITURE: 'F',
  JUDGMENT: 'J',
  DISCHARGE: 'D'
};

// Configuration for unexecuted transfer powers
export const UNEXECUTED_TRANSFER_CONFIG = {
  statusCode: 'UT',
  statusLabel: 'Unexecuted Transfer',
  badgeColor: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  requiresBatchReference: true
};

// Human-readable labels for power status codes
export const STATUS_LABELS = {
  E: 'Executed',
  T: 'Transfer',
  D: 'Discharged',
  F: 'Forfeiture',
  J: 'Judgment',
  V: 'Void',
  U: 'Unexecuted',
  UT: 'Unexecuted Transfer',
  UF: 'Unexecuted Forfeiture',
  UJ: 'Unexecuted Judgment',
  UD: 'Unexecuted Discharge',
  MISSING: 'Not Found'
};

// Power statuses that can be assigned to a report (unexecuted variants only)
export const REPORTABLE_STATUSES = ['U', 'UF', 'UJ', 'UD'];
export const isPowerReportable = (statusCd) => REPORTABLE_STATUSES.includes(statusCd);

// Valid statuses for rewrite originals (executed powers not in current report)
export const REWRITE_ORIGINAL_STATUSES = ['E', 'T', 'F', 'J', 'D'];

// Statuses that require case number validation (Forfeiture and Judgment)
export const CASE_NUMBER_REQUIRED_STATUSES = ['F', 'J'];

// States that require special handling for F/J rewrites (don't discharge original)
export const SPECIAL_DISCHARGE_STATES = ['NC', 'TX'];

// Report status strings
export const REPORT_STATUS = {
  DRAFT: 'DRAFT',
  SUBMITTED: 'SUBMITTED',
  PENDING: 'PENDING',
  PAID: 'PAID',
  COMPLETE: 'Complete',
  PROCESSING: 'Processing'
};

// Payment methods
export const PAYMENT_METHOD = {
  CHECK: 'Check',
  ACH: 'ACH'
};

// Rate priority levels
export const RATE_PRIORITY = {
  STANDARD: 0,  // Standard rate (default, automatically selected)
  VARIABLE: 1   // Variable rate (requires user selection and proof attributes)
};

// Rate type codes
export const RATE_TYPE = {
  PERCENTAGE: 'P',  // Premium calculated as percentage of bond amount
  FLAT: 'F'         // Premium is a flat fee regardless of bond amount
};

// Void ACH Payment settings (legacy NACHA-based approach)
export const VOID_ACH = {
  CUTOFF_HOUR: 23,
  CUTOFF_MINUTE: 57,
  CUTOFF_TIMEZONE: 'America/New_York',

  WINDOW_HOURS: 24,

  // Check if a report can be voided — allowed only while NACHA file hasn't been created
  // dateSubmitted is an optional frontend safety net for stale API data
  canVoid: (electronicPayment, isAchProcessed, dateSubmitted) => {
    if (!electronicPayment) return false; // Only ACH payments can be voided
    if (isAchProcessed) return false; // Can void only if NACHA file hasn't been created yet
    // Frontend safety: reject reports older than 24 hours even if API says not processed
    if (dateSubmitted) {
      const hoursSince = (Date.now() - new Date(dateSubmitted).getTime()) / (1000 * 60 * 60);
      if (hoursSince > 24) return false;
    }
    return true;
  },

  // Compute next NACHA cutoff (23:57 ET on a weekday) client-side
  getNextCutoff: () => {
    const now = new Date();
    const etTime = now.toLocaleTimeString('en-GB', { timeZone: 'America/New_York', hour12: false });
    const [h, m] = etTime.split(':').map(Number);
    const etDay = now.toLocaleDateString('en-US', { timeZone: 'America/New_York', weekday: 'short' });

    const nowMins = h * 60 + m;
    const cutoffMins = 23 * 60 + 57;

    let daysToAdd = 0;
    if (nowMins >= cutoffMins || etDay === 'Sat' || etDay === 'Sun') {
      daysToAdd = 1;
      if (etDay === 'Fri' && nowMins >= cutoffMins) daysToAdd = 3;
      else if (etDay === 'Sat') daysToAdd = 2;
      else if (etDay === 'Sun') daysToAdd = 1;
    }

    const minutesUntil = daysToAdd === 0
      ? cutoffMins - nowMins
      : (24 * 60 - nowMins) + (daysToAdd - 1) * 24 * 60 + cutoffMins;

    return new Date(now.getTime() + minutesUntil * 60 * 1000);
  },

  // Get remaining time until NACHA cutoff — uses API value or falls back to client-side calculation
  getRemainingTime: (nachaCutoffUtc) => {
    // Use API value if available, otherwise compute client-side
    const cutoff = nachaCutoffUtc ? new Date(nachaCutoffUtc) : VOID_ACH.getNextCutoff();
    const now = new Date();
    const remainingMs = cutoff - now;
    if (remainingMs <= 0) return null;
    const hours = Math.floor(remainingMs / (1000 * 60 * 60));
    const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
    return { hours, minutes, formatted: `${hours}h ${minutes}m` };
  }
};

// Validation rules
export const VALIDATION_RULES = {
  POWER_NUMBER_LENGTH: 9,
  MIN_DEFENDANT_AGE: 11,
  MAX_EXECUTION_DATE_YEARS_BACK: 20,
  SSN_PATTERN: /^\d{3}-\d{2}-\d{4}$/,
  MAX_CASE_NUMBER_LENGTH: 25,
  MAX_DEFENDANT_NAME_LENGTH: 20,
  MAX_SSN_LENGTH: 11,
  MAX_OFFENSE_LENGTH: 255
};

// Pagination options
export const PAGINATION_OPTIONS = {
  PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [10, 25, 50]
};

// Tab identifiers
export const TABS = {
  ACTIVE: 'active',
  RECENT: 'recent',
  BATCHES: 'batches',
  RECEIPT: 'receipt'
};

// Debounce delay for search (ms)
export const SEARCH_DEBOUNCE_MS = 400;

// Rewrite types enum
export const REWRITE_TYPES = {
  ONE_TO_ONE: 'ONE_TO_ONE',
  MANY_TO_ONE: 'MANY_TO_ONE',
  ONE_TO_MANY: 'ONE_TO_MANY'
};

// Rewrite type configuration
export const REWRITE_TYPE_CONFIG = {
  [REWRITE_TYPES.ONE_TO_ONE]: {
    label: 'One-to-One',
    shortLabel: '1:1',
    description: 'Replace 1 executed power with 1 new power',
    supported: true,
    minOriginals: 1,
    maxOriginals: 1,
    minReplacements: 1,
    maxReplacements: 1
  },
  [REWRITE_TYPES.MANY_TO_ONE]: {
    label: 'Many-to-One',
    shortLabel: 'N:1',
    description: 'Combine multiple executed powers into 1 new power',
    supported: true,
    minOriginals: 2,
    maxOriginals: null, // No max
    minReplacements: 1,
    maxReplacements: 1
  },
  [REWRITE_TYPES.ONE_TO_MANY]: {
    label: 'One-to-Many',
    shortLabel: '1:N',
    description: 'Split 1 executed power into multiple new powers',
    supported: true,
    minOriginals: 1,
    maxOriginals: 1,
    minReplacements: 2,
    maxReplacements: null // No max
  }
};

// Rewrite status (for a power)
export const REWRITE_STATUS = {
  NONE: 'none',           // Power has not been rewritten and is not a rewrite
  IS_REWRITE: 'is_rewrite',     // Power is a rewrite (replacement power)
  WAS_REWRITTEN: 'was_rewritten' // Power was rewritten to another power (original)
};

// Transaction type codes
export const TRANSACTION_TYPE = {
  EXECUTE: 'E',
  REWRITE: 'R',
  DISCHARGE: 'D',
  VOID: 'V',
  FORFEITURE: 'F',
  JUDGMENT: 'J',
  SET_ASIDE: 'S',
  TRANSFER: 'T'
};

// Rewrite wizard steps - 5 step flow
export const REWRITE_STEPS = {
  SELECT_TYPE: 0,        // Step 0: Select rewrite type (One-to-One, Many-to-One, One-to-Many)
  SELECT_ORIGINALS: 1,   // Step 1: Select original power(s) to replace
  SELECT_REPLACEMENT: 2, // Step 2: Select replacement power(s) from inventory
  ENTER_DETAILS: 3,      // Step 3: Enter defendant/agent/city details
  REVIEW: 4              // Step 4: Review and confirm
};

// ============================================
// RECEIPT OF POWERS
// ============================================

// Temporary feature flags for Receipt of Powers.
// Flip these back to `true` to restore the hidden UI.
export const RECEIPT_FEATURES = {
  ORDER_DRILLDOWN: false,   // per-order detail modal that lists each individual power
  SHOW_POWER_COUNT: false   // "# Powers" column (desktop) / "Powers" field (mobile)
};

// Receipt status
export const RECEIPT_STATUS = {
  PENDING: 'pending',
  RECEIVED: 'received'
};

// Validation rules for confirmation codes
export const RECEIPT_VALIDATION = {
  CODE_LENGTH: 4,
  CODE_PATTERN: /^[A-Za-z0-9]{4}$/,
  ADMIN_CODE_PREFIX: 'code',
  ADMIN_CODE_LENGTH: 11  // "code-283947" is 11 chars
};

// User-facing messages for receipt operations
export const RECEIPT_MESSAGES = {
  NO_PENDING: "You don't have any pending power shipments to confirm.",
  HAS_PENDING: (count) => `You have ${count} shipment${count !== 1 ? 's' : ''} waiting for confirmation.`,
  INVALID_CODE: 'The confirmation code is invalid. Please check and try again.',
  ALREADY_CONFIRMED: 'This receipt of powers has already been confirmed.',
  SUCCESS: (count) => `Receipt confirmed! ${count} power${count !== 1 ? 's' : ''} have been acknowledged.`,
  NETWORK_ERROR: 'Unable to confirm receipt. Please try again.',
  CODE_HELP: 'Enter the 4-character code from inside your shipment box. The code is case-sensitive.',
  CASE_SENSITIVE_WARNING: 'Confirmation codes are case-sensitive. Make sure to enter the code exactly as shown.'
};

// Power proof upload constraints — must stay in sync with the backend allowlist
// in OathPowerReportsController.UploadProofImage (JPEG/PNG/GIF/PDF, 10MB)
export const PROOF_FILE_CONFIG = {
  MAX_SIZE_MB: 10,
  MAX_SIZE_BYTES: 10 * 1024 * 1024,
  ACCEPTED_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'],
  ACCEPTED_EXTENSIONS: '.jpg,.jpeg,.png,.gif,.pdf',
};
